import { AlertTriangle, ArrowLeft, Home } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Spinner } from '@/components/ui';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info);
    this.setState({ errorInfo: info });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-sm text-gray-500 mb-2 max-w-md">
            An unexpected error occurred while rendering this page.
          </p>
          {this.state.error?.message && (
            <p className="text-xs text-gray-400 mb-6 max-w-md font-mono bg-gray-50 rounded px-3 py-2">
              {this.state.error.message}
            </p>
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                this.setState({ hasError: false, error: undefined, errorInfo: undefined });
              }}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md
                         text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Try Again
            </button>
            <Button
              variant="primary"
              onClick={() => {
                this.setState({ hasError: false, error: undefined, errorInfo: undefined });
                window.location.href = '/dashboard';
              }}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-md text-sm font-medium hover: transition-colors"
            >
              <Home className="w-4 h-4" />
              Go to Dashboard
            </Button>
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md
                         text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              <Spinner size="sm" className="w-4 h-4" />
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
